import * as pageTransitions from "pageTransitions";

export type NavigateOptions = {
  trigger?: boolean;
  replace?: boolean;
};

type RouteParameters = Array<string | null>;

export type HandlerFunctionType = (this: AppRouter, ...args: any[]) => unknown;

export type RouteMap = Record<string, HandlerFunctionType>;

type BrowserWindow = Pick<Window, "addEventListener" | "location" | "history">;

export interface AppRouterLike {
  navigate(path: string, options?: NavigateOptions): void;
}

const optionalParam = /\((.*?)\)/g;
const namedParam = /(\(\?)?:\w+/g;
const splatParam = /\*\w+/g;
const escapeRegExp = new RegExp("[-{}\\[\\]+?.,\\\\^$|#\\s]", "g");

const routeToRegExp = (route: string) =>
  new RegExp(
    "^" +
      route
        .replace(escapeRegExp, "\\$&")
        .replace(optionalParam, "(?:$1)?")
        .replace(namedParam, (match, optional) =>
          optional ? match : "([^/?]+)"
        )
        .replace(splatParam, "([^?]*?)") +
      "(?:\\?([\\s\\S]*))?$"
  );

const getFragment = (path: string) => {
  const withoutHash = path.split("#")[0];
  return withoutHash.replace(/^\/+/, "");
};

const extractParameters = (route: RegExp, fragment: string) => {
  const parameters = route.exec(fragment)?.slice(1) ?? [];
  return parameters.map((parameter, index) => {
    if (index === parameters.length - 1) {
      return parameter || null;
    }
    return parameter ? decodeURIComponent(parameter) : null;
  });
};

export default class AppRouter {
  private readonly compiledRoutes: Array<{
    route: RegExp;
    handler: HandlerFunctionType;
  }>;

  private readonly browserWindow: BrowserWindow;

  private readonly onPopState = () => {
    void this.dispatch(this.currentPath());
  };

  private started = false;

  constructor(routes: RouteMap, browserWindow: BrowserWindow = window) {
    this.compiledRoutes = Object.entries(routes).map(([route, handler]) => ({
      route: routeToRegExp(route),
      handler,
    }));
    this.browserWindow = browserWindow;
  }

  start() {
    if (this.started) {
      return;
    }

    this.browserWindow.addEventListener("popstate", this.onPopState);
    this.started = true;
    void this.dispatch(this.currentPath());
  }

  navigate(path: string, options: NavigateOptions = {}) {
    const normalizedPath = this.normalizePath(path);

    // Navigating to the current URL mustn't add a history entry, or Back
    // would return to the same page (which can then navigate there again)
    const index = this.historyIndex();
    if (options.replace || normalizedPath === this.currentPath()) {
      this.browserWindow.history.replaceState({ index }, "", normalizedPath);
    } else {
      this.browserWindow.history.pushState(
        { index: index + 1 },
        "",
        normalizedPath
      );
    }
    // Its own URL, if the page shown has moved (a new page notes itself too)
    pageTransitions.followURL();

    if (options.trigger) {
      void this.dispatch(normalizedPath);
    }
  }

  /**
   * How many pages of this visit there are before this one in the history
   * (pageTransitions.ts finds the page to go back to by it)
   */
  private historyIndex(): number {
    return this.browserWindow.history.state?.index ?? 0;
  }

  protected execute(callback: HandlerFunctionType, args: RouteParameters) {
    return callback.apply(this, args);
  }

  private currentPath() {
    return (
      this.browserWindow.location.pathname + this.browserWindow.location.search
    );
  }

  private normalizePath(path: string) {
    if (path.startsWith("/")) {
      return path;
    }
    return "/" + path;
  }

  private async dispatch(path: string) {
    const fragment = getFragment(path);

    for (const { route, handler } of this.compiledRoutes) {
      if (!route.test(fragment)) {
        continue;
      }

      await this.execute(handler, extractParameters(route, fragment));
      return;
    }
  }
}
